import { useBusinessEntitiesOfCompany } from '@commenda/api';
import {
  CountryCode,
  FlagComponent,
  Icons,
  InputErrorMessage,
  Label,
  SingleCombobox,
  Tooltip,
  useToggle,
} from '@commenda/kakashi';
import { getEntityDisplayName } from '@commenda/utils';
import { AddEntityModal } from '../../Corporation/common/BusinessEntity/AddEntityModal';

export interface CorporationSelectorProps {
  companyId: number;
  excludedCorporationIds?: number[];
  filterIncorporationInProgress?: boolean;
  label: string;
  placeholder?: string;
  value: number | null;
  onChange: (corporationId: number) => void;
  error?: string;
  disabled?: boolean;
  showAddEntity?: boolean;
  helpText?: string;
  /** Links the label to the picker. */
  id?: string;
}

export const CorporationSelector = ({
  companyId,
  excludedCorporationIds = [],
  filterIncorporationInProgress = false,
  label,
  placeholder = 'Search or select entity',
  value,
  onChange,
  error,
  disabled = false,
  showAddEntity = true,
  helpText,
  id,
}: CorporationSelectorProps) => {
  const addEntityModalVisibility = useToggle();

  const { data: businessEntities, refetch } =
    useBusinessEntitiesOfCompany(companyId);

  const filteredEntities =
    businessEntities?.entities.filter(
      (entity) =>
        !excludedCorporationIds.includes(entity.id) &&
        (!filterIncorporationInProgress ||
          !entity.featureSpecificInfo?.incorporation?.isIncorporationInProgress)
    ) ?? [];

  const selectedEntity = businessEntities?.entities.find(
    (entity) => entity.id === value
  );

  return (
    <div className="flex flex-col gap-xs w-full">
      <div className="flex items-center gap-xs">
        <Label htmlFor={id}>{label}</Label>
        {helpText && (
          <Tooltip content={helpText} side="top">
            <Icons.InfoCircle className="size-4 text-text-tertiary" />
          </Tooltip>
        )}
      </div>

      <AddEntityModal
        isOpen={addEntityModalVisibility.visible}
        onClose={addEntityModalVisibility.hide}
        companyId={companyId}
        onSuccess={async (entity) => {
          await refetch();
          onChange(entity.id);
          addEntityModalVisibility.hide();
        }}
      />

      <SingleCombobox
        id={id}
        disabled={disabled}
        options={[
          ...(showAddEntity
            ? [
                {
                  label: 'Add new entity',
                  value: 'add-new-entity',
                },
              ]
            : []),
          ...filteredEntities.map((entity) => ({
            label: getEntityDisplayName({
              legalName: entity.generalCorporationInfo?.legalName,
              incorporationCountry:
                entity.generalCorporationInfo?.incorporationCountry,
            }),
            value: entity.id.toString(),
          })),
        ]}
        value={value?.toString() ?? ''}
        setValue={(entityId: string) => {
          if (entityId === 'add-new-entity') {
            addEntityModalVisibility.show();
          } else {
            onChange(+entityId);
          }
        }}
        canClearSelection={false}
        placeholder={placeholder}
        searchPlaceholder="Search for an entity"
        _className={error ? 'border-border-error' : ''}
        renderTrigger={(props) => {
          if (!props.selectedOption?.value || !selectedEntity) {
            return (
              <span className="txt-small-regular text-text-placeholder truncate">
                {placeholder}
              </span>
            );
          }

          const incorporationCountry =
            selectedEntity?.generalCorporationInfo?.incorporationCountry;

          return (
            <div className="flex items-center gap-md min-w-0">
              <FlagComponent
                country={incorporationCountry as CountryCode}
                countryName={incorporationCountry}
                _className="size-4 shrink-0"
              />
              <span className="txt-small-regular text-text-primary truncate">
                {props.selectedOption.label}
              </span>
            </div>
          );
        }}
        renderOption={({ isSelected, option, onSelect }) => {
          const businessEntity = businessEntities?.entities.find(
            (entity) => entity.id === +option.value
          );

          const incorporationCountry =
            businessEntity?.generalCorporationInfo?.incorporationCountry;

          if (option.value === 'add-new-entity') {
            return (
              <div className="flex items-center justify-between w-full py-0.5">
                <div className="flex items-center gap-md w-[80%]">
                  <Icons.Plus className="size-4 shrink-0 text-text-secondary" />
                  <span className="txt-small-medium text-text-secondary truncate">
                    Add new entity
                  </span>
                </div>
                <div className="w-4 shrink-0">
                  {isSelected && <Icons.Check className="size-4" />}
                </div>
              </div>
            );
          }

          return (
            <div
              className="flex items-center justify-between w-full py-0.5"
              onClick={() => {
                if (option.value === value?.toString()) return;
                onSelect(option.value);
              }}
            >
              <div className="flex items-center gap-md w-[80%]">
                <FlagComponent
                  country={incorporationCountry as CountryCode}
                  countryName={incorporationCountry}
                  _className="size-5 shrink-0"
                />
                <span className="txt-small-regular text-text-primary truncate">
                  {option.label}
                </span>
              </div>
              <div className="w-4 shrink-0">
                {isSelected && (
                  <Icons.Check className="size-4 text-text-primary" />
                )}
              </div>
            </div>
          );
        }}
      />

      {error && <InputErrorMessage message={error} />}
    </div>
  );
};
